<section class="salesforce-mapping">
  <p class="info-text">
    Map AwesomeDocs entities to the Salesforce objects that exist in this org. Enable mapping to surface it in pickers and add key
    fields needed when generating documents.
  </p>

  <div class="mapping-actions">
    <button theme="small" type="button" (click)="addMapping()">Add mapping</button>
  </div>

  @if (isLoading) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  }

  @for (mapping of mappings; track $index; let i = $index) {
    <article
      class="mapping-wrapper"
      [class.mapping-invalid]="hasMappingTemplateInvalidTokens(i) || hasMappingInvalidFieldPaths(i)"
      #mappingCard
    >
      <div class="mapping-summary-row">
        <button class="mapping-summary-main" type="button" (click)="toggleMapping(i)">
          <span class="disclosure-chevron" [class.expanded]="isMappingExpanded(i)">▸</span>
          <strong>{{ getMappingTitle(i) }}</strong>
          <span class="mapping-summary-meta">{{ getMappingSummary(i) }}</span>
          @if (hasMappingTemplateInvalidTokens(i)) {
            <span class="mapping-summary-error">
              Invalid display template tokens: {{ getMappingTemplateInvalidTokensText(i) }}
            </span>
          }
          @if (hasMappingInvalidFieldPaths(i)) {
            <span class="mapping-summary-error">One or more field paths are invalid</span>
          }
        </button>
        <div class="actions-cell">
          <mat-slide-toggle [(ngModel)]="mapping.enabled" (change)="onToggleChange()">Enabled</mat-slide-toggle>
          <button theme="small" type="button" (click)="removeMapping(i)" [disabled]="mappings.length === 1">Remove</button>
        </div>
      </div>

      @if (isMappingExpanded(i)) {
        <table class="table-full-width table-striped table-td-border-right full-width-table">
          <tbody>
            <tr>
              <td>Salesforce object</td>
              <td>
                <div class="sf-autocomplete-wrapper">
                  <input
                    type="text"
                    class="full-width sf-autocomplete-input"
                    placeholder="Search objects..."
                    [value]="getSearchTerm(i) || getObjectDisplay(mapping.objectName)"
                    (input)="onAutocompleteInput(i, $event.target?.value)"
                    [matAutocomplete]="sfAuto"
                  />
                  <mat-autocomplete
                    #sfAuto="matAutocomplete"
                    [displayWith]="objectDisplay"
                    (optionSelected)="onAutocompleteSelected(i, $event)"
                    class="sf-mat-panel"
                  >
                    @if (!getFilteredObjects(i).length) {
                      <mat-option disabled>
                        No objects found
                      </mat-option>
                    }
                    @for (option of getFilteredObjects(i); track trackByOptionName($index, option)) {
                      <mat-option [value]="option.apiName ?? option.name">
                        {{ getOptionDisplay(option) || 'Unknown' }}
                      </mat-option>
                    }
                  </mat-autocomplete>
                </div>
              </td>
            </tr>
            <tr>
              <td>Primary display template</td>
              <td>
                <textarea
                  class="template-input"
                  [class.invalid]="hasTemplateInvalidTokens(i, 'primary')"
                  rows="2"
                  placeholder="{{'{{'}}Name{{'}}'}} - {{'{{'}}Account.Name{{'}}'}}"
                  [(ngModel)]="mapping.display.primary"
                  (ngModelChange)="onModelChange()"
                ></textarea>
                <p class="template-hint">
                  Use double braces to reference mapped fields (e.g. {{'{{'}}Name{{'}}'}}). Click a field to insert:
                  @if (mapping.fields?.length) {
                    @for (path of getTemplateFieldPaths(i); track path) {
                      <button
                        class="field-chip token-chip-btn"
                        type="button"
                        (click)="insertTemplateToken(i, 'primary', path)"
                      >
                        {{ path }}
                      </button>
                    }
                  } @else {
                    <span class="muted">Add field mappings above to unlock tokens.</span>
                  }
                </p>
                @if (hasTemplateInvalidTokens(i, 'primary')) {
                  <p class="template-error">
                    Invalid tokens: {{ getTemplateInvalidTokensText(i, 'primary') }}
                  </p>
                }
              </td>
            </tr>
            <tr>
              <td>Secondary display template</td>
              <td>
                <textarea
                  class="template-input"
                  [class.invalid]="hasTemplateInvalidTokens(i, 'secondary')"
                  rows="2"
                  placeholder="{{'{{'}}Account.Name{{'}}'}} – {{'{{'}}StageName{{'}}'}}"
                  [(ngModel)]="mapping.display.secondary"
                  (ngModelChange)="onModelChange()"
                ></textarea>
                <p class="template-hint">
                  Optional line shown as supporting details wherever this entity appears. Click a field to insert:
                  @if (mapping.fields?.length) {
                    @for (path of getTemplateFieldPaths(i); track path) {
                      <button
                        class="field-chip token-chip-btn"
                        type="button"
                        (click)="insertTemplateToken(i, 'secondary', path)"
                      >
                        {{ path }}
                      </button>
                    }
                  }
                </p>
                @if (hasTemplateInvalidTokens(i, 'secondary')) {
                  <p class="template-error">
                    Invalid tokens: {{ getTemplateInvalidTokensText(i, 'secondary') }}
                  </p>
                }
              </td>
            </tr>
            <tr>
              <td>Related entities</td>
              <td>
                <div class="related-entities-section">
                  @if (getPossibleRelatedEntitiesLoading(i)) {
                    <p class="related-entities-hint">Loading direct relationships...</p>
                  } @else if (getPossibleRelatedEntitiesError(i); as relatedEntitiesError) {
                    <p class="template-error">{{ relatedEntitiesError }}</p>
                  } @else if (getPossibleRelatedEntities(i).length) {
                    <div class="related-entity-list">
                      @for (candidate of getPossibleRelatedEntities(i); track candidate.objectName) {
                        <div class="related-entity-card">
                          <div class="related-entity-card-header">
                            <strong>{{ getRelatedEntityDisplay(candidate) }}</strong>
                            <span>{{ getRelatedEntityDetail(candidate) }}</span>
                          </div>
                          <div class="related-entity-option-list">
                            @for (relation of candidate.relationSegments; track relation.segment) {
                              <button
                                class="related-entity-option"
                                type="button"
                                [class.selected]="isRelatedEntityPathSelected(i, candidate.objectName, relation.segment)"
                                (click)="selectRelatedEntityPath(i, candidate.objectName, relation.segment)"
                              >
                                {{ relation.label }} ({{ relation.segment }})
                              </button>
                            }
                          </div>
                        </div>
                      }
                    </div>
                    <p class="related-entities-hint">
                      Related entities are discovered from enabled Salesforce mappings only. Select the relationship field to use for each related object.
                      These are used to determine Destination, Source entity, and Template section options.
                    </p>
                  } @else {
                    <p class="related-entities-hint">
                      No direct related enabled Salesforce mappings found for this object.
                    </p>
                  }
                </div>
              </td>
            </tr>
            <tr>
              <td class="field-label">Field mappings</td>
              <td>
                <div class="field-mappings-section" [class.invalid]="hasMappingTemplateInvalidTokens(i)">
                  <div class="field-header">
                    <button theme="small" type="button" (click)="addField(i)">Add field</button>
                  </div>
                  @if (hasMappingTemplateInvalidTokens(i)) {
                    <p class="template-error field-mappings-error">
                      Display template contains invalid tokens: {{ getMappingTemplateInvalidTokensText(i) }}. Fix template tokens or add matching field mappings.
                    </p>
                  }
                  @if (!mapping.fields.length) {
                    <div class="no-fields">
                      No fields configured yet. Add one or select an object to start.
                    </div>
                  }
                  <div class="field-items">
                    @for (field of mapping.fields; track $index; let fieldIndex = $index) {
                      <div
                        class="field-item"
                        [class.expanded]="isFieldExpanded(i, fieldIndex)"
                        [class.template-invalid]="hasMappingTemplateInvalidTokens(i)"
                        [class.path-invalid]="!field.path?.trim() || isBuiltPathInvalid(i, fieldIndex)"
                      >
                        <div class="field-item-summary">
                          <button class="field-summary-btn" type="button" (click)="toggleField(i, fieldIndex)">
                            <span class="disclosure-chevron small" [class.expanded]="isFieldExpanded(i, fieldIndex)">▸</span>
                            <span class="field-summary-title">
                              @if (!field.path?.trim() || isBuiltPathInvalid(i, fieldIndex)) {
                                <mat-icon class="field-invalid-icon">warning</mat-icon>
                              }
                              {{ getFieldTitle(i, fieldIndex) }}
                            </span>
                            <span class="field-summary-meta">{{ getFieldSummary(i, fieldIndex) }}</span>
                          </button>
                          <button class="inline-remove icon-btn" type="button" (click)="removeField(i, fieldIndex)">
                            <mat-icon>close</mat-icon>
                          </button>
                        </div>

                        @if (isFieldExpanded(i, fieldIndex)) {
                          <div class="field-row">
                            <app-salesforce-path-builder-picker
                              [mappingIndex]="i"
                              [fieldIndex]="fieldIndex"
                              [field]="field"
                              [api]="pathBuilderApi"
                              [maxAssociatedEntityDepth]="maxAssociatedEntityDepth"
                              [pathDropdownPositions]="pathDropdownPositions">
                            </app-salesforce-path-builder-picker>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      }
    </article>
  }

</section>
